import React, { useState, useEffect } from 'react';
import ReleaseNotesDialog from './index';
import { releaseNotesApi, ReleaseNote } from '../../utils/api';
import { OFFLINE_ENABLED, skipWhenOffline } from '../../offline';
import { isTransportError } from '../../offline/connectivity';

interface ReleaseNotesProviderProps {
  children: React.ReactNode;
}

const ReleaseNotesProvider: React.FC<ReleaseNotesProviderProps> = ({ children }) => {
  const [releaseNotes, setReleaseNotes] = useState<ReleaseNote[]>([]);
  const [shouldShow, setShouldShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const checkForReleaseNotes = async () => {
    // Offline (flag-gated): skip the doomed shouldShow request entirely — the
    // dialog just doesn't auto-show this session and re-checks next launch.
    if (!skipWhenOffline()) return;
    setLoading(true);
    setError(null);

    try {
      const response = await releaseNotesApi.shouldShow();
      if (!response.shouldShow) {
        setShouldShow(false);
        setReleaseNotes([]);
        return;
      }
      // missedNotes is absent from a server older than this web build.
      setReleaseNotes(
        response.missedNotes?.length
          ? response.missedNotes
          : response.releaseNote
            ? [response.releaseNote]
            : [],
      );

      // Welcome modal (first-login) takes priority. Give it a moment to
      // decide whether it's going to render, then wait for any open dialog
      // to close before we show the release notes.
      await new Promise((resolve) => setTimeout(resolve, 1500));
      await waitForNoOpenDialogs();
      setShouldShow(true);
    } catch (err: any) {
      console.error('Error checking for release notes:', err);
      setError('Failed to check for release notes');
      setShouldShow(false);
      setReleaseNotes([]);
    } finally {
      setLoading(false);
    }
  };

  const waitForNoOpenDialogs = (): Promise<void> =>
    new Promise((resolve) => {
      let elapsed = 0;
      const check = () => {
        const dialogs = document.querySelectorAll('.MuiDialog-container').length;
        if (dialogs === 0 || elapsed >= 60_000) {
          resolve();
          return;
        }
        elapsed += 500;
        setTimeout(check, 500);
      };
      check();
    });

  const markReleaseNoteSeen = async (id: number) => {
    try {
      await releaseNotesApi.markSeen(id);
      setShouldShow(false);
      setReleaseNotes([]);
    } catch (err: any) {
      console.error('Error marking release note as seen:', err);
      // Transport failure (e.g. went offline with the dialog open): dismiss
      // locally — worst case the note re-shows once, and the server "seen"
      // flag catches up the next time it's marked online.
      if (OFFLINE_ENABLED && isTransportError(err)) {
        setShouldShow(false);
        setReleaseNotes([]);
        return;
      }
      setError('Failed to mark release note as seen');
    }
  };

  const dismissReleaseNote = () => {
    setShouldShow(false);
    setReleaseNotes([]);
    setError(null);
  };

  // Marking the newest note seen covers every older one too (the server keeps one version per user).
  const handleMarkSeen = () => {
    const newest = releaseNotes[releaseNotes.length - 1];
    if (newest) {
      markReleaseNoteSeen(newest.id);
    }
  };

  // Auto-check on mount
  useEffect(() => {
    checkForReleaseNotes();
  }, []);

  return (
    <>
      {children}
      <ReleaseNotesDialog
        open={shouldShow}
        releaseNotes={releaseNotes}
        onClose={dismissReleaseNote}
        onMarkSeen={handleMarkSeen}
        loading={loading}
        error={error}
      />
    </>
  );
};

export default ReleaseNotesProvider;
